<template>

    <v-dialog v-model="showDialog" persistent max-width="600px">

        <v-sheet rounded class="dialog">

            <v-card
                elevation="0"
                class="pa-5 font-weight-bold rounded-b-0"
                color="headerDialogColor"
            >
                {{ $t("direct_outputs.edit_last_entry") }}
            </v-card>

            <v-card elevation="0" class="pa-5">

                <v-form ref="logistical_lot_form">

                    <v-row>

                        <v-col cols="12">
                            <v-text-field
                                v-model="logisticalLot.container_quantity"
                                dense
                                outlined
                                light
                                :label="$t('direct_outputs.output_quantity')"
                                hide-details="auto"
                                :rules="containerQuantityRules()"
                            />
                        </v-col>

                        <v-col cols="12" class="font-weight-bold">
                            {{ $t("direct_outputs.include_services") }}
                        </v-col>

                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="logisticalLot.frosting_quantity"
                                dense
                                outlined
                                light
                                :label="$t('direct_outputs.frosting')"
                                hide-details="auto"
                                :disabled="!containerQuantityIsValid"
                                :rules="containerServiceRules(logisticalLot.frosting)"
                            />
                        </v-col>

                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="logisticalLot.filming_quantity"
                                dense
                                outlined
                                light
                                :label="$t('direct_outputs.wrapping')"
                                hide-details="auto"
                                :disabled="!containerQuantityIsValid"
                                :rules="containerServiceRules(logisticalLot.filming)"
                            />
                        </v-col>

                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="logisticalLot.pallet_quantity"
                                dense
                                outlined
                                light
                                :label="$t('direct_outputs.pallet')"
                                hide-details="auto"
                                :disabled="!containerQuantityIsValid"
                                :rules="containerServiceRules(logisticalLot.pallet)"
                            />
                        </v-col>

                    </v-row>

                </v-form>

            </v-card>

            <v-divider></v-divider>

            <v-card elevation="0" class="pa-5 d-flex align-items-center">

                <v-btn
                    elevation="0"
                    text
                    @click="showDialog = false"
                >
                    {{ $t("global.cancel") }}
                </v-btn>

                <v-spacer></v-spacer>

                <v-btn
                    color="primary"
                    @click="saveDeliverableLogisticalLot"
                >
                    {{ $t("direct_outputs.tag") }}
                </v-btn>

                <v-btn
                    class="ml-1"
                    color="primary"
                    @click="saveDeliveredLogisticalLot"
                >
                    {{ $t("direct_outputs.validate") }}
                </v-btn>

            </v-card>

        </v-sheet>

    </v-dialog>

</template>

<script>
export default {
    name: "EditEntry",
    props: {
        visible: {
            type: Boolean,
            required: true,
            default: false,
        },
        action: {
            type: String,
            required: true,
            default: "edit",
        },
        totalCostalDayContainerQuantity: {
            type: Number,
            required: true,
            default: null
        },
        outCostalDayContainerQuantity: {
            type: Number,
            required: true,
            default: null
        },
        buyerId: {
            type: Number,
            required: false,
            default: null
        },
        logisticalLotToEdit: {
            required: true,
            type: Object,
            default: () => {
                return {
                    buyer_id: null,
                    container_quantity: null,
                    frosting: null,
                    filming: null,
                    pallet: null,
                }
            }
        }
    },
    data() {
        return {
            containerQuantityIsValid: false,
            logisticalLot: {}
        };
    },
    computed: {
        showDialog: {
            get() {
                return this.visible;
            },
            set(value) {
                if (!value) {
                    this.$emit("close");
                }
            },
        },
    },
    watch: {
        logisticalLotToEdit: {
            handler(logisticalLot) {
                this.logisticalLot = Object.assign({}, logisticalLot);
            },
            deep: true,
        },
    },
    created() {
        this.logisticalLot = Object.assign({}, this.logisticalLotToEdit);
    },
    methods: {
        containerQuantityRules() {
            if (this.logisticalLot.container_quantity === null) {
                this.containerQuantityIsValid = false;
                return [this.$tc('validation.required', this.$t('direct_outputs.output_quantity'), {field_name: this.$t('direct_outputs.output_quantity')})];
            }

            if (String(this.logisticalLot.container_quantity).trim().length <= 0) {
                this.containerQuantityIsValid = false;
                return [this.$tc('validation.required', this.$t('direct_outputs.output_quantity'), {field_name: this.$t('direct_outputs.output_quantity')})];
            }

            if (!!isNaN(this.logisticalLot.container_quantity)) {
                this.containerQuantityIsValid = false;
                return [this.$t('validation.numerical_field')];
            }

            const remainingContainers = this.totalCostalDayContainerQuantity - this.outCostalDayContainerQuantity + this.logisticalLotToEdit.container_quantity;
            if (this.logisticalLot.container_quantity > remainingContainers) {
                this.containerQuantityIsValid = false;
                return [this.$t('validation.outbound_containers')];
            }

            this.containerQuantityIsValid = true;

            return [];
        },
        containerServiceRules(serviceQuantity) {
            if (serviceQuantity !== null) {
                if (!!isNaN(serviceQuantity)) {
                    return [this.$t('validation.numerical_field')];
                }
                if (serviceQuantity > this.logisticalLot.container_quantity) {
                    return [this.$t('validation.outbound_services')];
                }
            }
        },
        async saveDeliverableLogisticalLot() {
            this.logisticalLot.delivery_service = 1;
            let logisticalLot = await this.save();
            if (!!logisticalLot) {
                await this.$emit('printSticker', logisticalLot.id);
                this.closeDialog();
            }
        },
        async saveDeliveredLogisticalLot() {
            this.logisticalLot.delivery_service = 0;
            await this.save();
            this.closeDialog();
        },
        async save() {
            let logisticalLot = null;
            try {
                this.loading = true;
                if (this.$refs.logistical_lot_form.validate()) {
                    if (this.action === "add") {
                        this.logisticalLot.buyer_id = this.buyerId
                        logisticalLot = (await this.$api.logisticalLot.create(this.logisticalLot)).data.data;
                    } else if (this.action === "edit") {
                        logisticalLot = (await this.$api.logisticalLot.edit(this.logisticalLot)).data.data;
                    }
                }
            } finally {
                this.loading = false;
            }
            return logisticalLot;
        },
        closeDialog() {
            this.clearInputs();
            this.$refs.logistical_lot_form.resetValidation();
            this.$emit('close');
        },
        clearInputs() {
            this.logisticalLot.buyer_id = null;
            this.logisticalLot.container_quantity = null;
            this.logisticalLot.frosting = null;
            this.logisticalLot.filming = null;
            this.logisticalLot.pallet = false;
        }
    }
};
</script>
